import { Button, ButtonLevel, ButtonSize, Menu, MenuItem, MenuPopover, MenuSeparator, MenuTrigger } from '@vizit/atoms';
import { ArrowNarrowUpRightIcon, DotsHorizontalIcon, DownloadMinimalIcon, Edit03Icon, Trash01Icon } from '@vizit/icons';
import { cx } from '@vizit/theme';

/** What can be done to a benchmark from its "⋯" menu. */
export enum BenchmarkAction {
  Edit = 'edit',
  OpenInNewTab = 'open-in-new-tab',
  Download = 'download',
  Delete = 'delete',
}

export interface BenchmarkActionsMenuProps {
  /**
   * The benchmark name, used in the trigger's accessible name
   */
  benchmarkName: string;
  /**
   * Called with the chosen action
   */
  onAction: (action: BenchmarkAction) => void;
  /**
   * Actions shown but not available yet
   *
   * @optional
   */
  disabledActions?: BenchmarkAction[];
  /**
   * Whether the menu starts open
   *
   * @optional
   * @default false
   */
  defaultOpen?: boolean;
  /**
   * Additional CSS classes for the trigger button
   *
   * @optional
   */
  className?: string;
}

/**
 * The "⋯" menu of a benchmark: Edit benchmark, Open in new tab, Download assets and, after a divider, the
 * destructive Delete benchmark.
 */
export function BenchmarkActionsMenu(props: BenchmarkActionsMenuProps) {
  const { benchmarkName, onAction, disabledActions, defaultOpen = false, className } = props;

  return (
    <MenuTrigger defaultOpen={defaultOpen}>
      <Button
        aria-label={`Actions for ${benchmarkName}`}
        level={ButtonLevel.Ghost}
        size={ButtonSize.Xs}
        iconStart={<DotsHorizontalIcon size={16} aria-hidden />}
        className={className}
      />
      <MenuPopover placement="bottom end">
        <Menu onAction={(key) => onAction(key as BenchmarkAction)} disabledKeys={disabledActions}>
          <MenuItem id={BenchmarkAction.Edit} textValue="Edit benchmark">
            <Edit03Icon size={14} aria-hidden />
            Edit benchmark
          </MenuItem>
          <MenuItem id={BenchmarkAction.OpenInNewTab} textValue="Open in new tab">
            <ArrowNarrowUpRightIcon size={14} aria-hidden />
            Open in new tab
          </MenuItem>
          <MenuItem id={BenchmarkAction.Download} textValue="Download assets">
            <DownloadMinimalIcon size={14} aria-hidden />
            Download assets
          </MenuItem>
          <MenuSeparator />
          <MenuItem
            id={BenchmarkAction.Delete}
            textValue="Delete benchmark"
            className={cx(
              'text-error-600 data-[focused]:bg-error-50 data-[focused]:text-error-700',
              '[&_svg]:text-error-600 [&[data-focused]_svg]:text-error-700',
            )}
          >
            <Trash01Icon size={14} aria-hidden />
            Delete benchmark
          </MenuItem>
        </Menu>
      </MenuPopover>
    </MenuTrigger>
  );
}
